import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import {
  detectIsInLti,
  detectIsLtiInstructor,
  LTI_CONTEXT_UPDATE_EVENT,
} from '../detection';

/**
 * Hook to detect if the app is running in an LTI context.
 *
 * Backed by the shared `detectIsInLti` helper so this hook, `isInLtiContext()`,
 * and any other consumer stay in sync. Re-evaluates on:
 *   - mount (synchronous initial state prevents first-render flash in e.g. ChannelTalk)
 *   - route changes (captures `/lti/*` paths and late `ltiUserId` params)
 *   - `LTI_CONTEXT_UPDATE_EVENT` (fired by LtiContextManager.initialize/clear)
 */
export function useIsInLti(): boolean {
  const router = useRouter();
  const [isInLti, setIsInLti] = useState(detectIsInLti);

  useEffect(() => {
    setIsInLti(detectIsInLti());
  }, [router.pathname, router.query.__debugLms, router.query.ltiUserId]);

  useEffect(() => {
    const handler = () => setIsInLti(detectIsInLti());
    window.addEventListener(LTI_CONTEXT_UPDATE_EVENT, handler);
    return () => window.removeEventListener(LTI_CONTEXT_UPDATE_EVENT, handler);
  }, []);

  return isInLti;
}

/**
 * Hook to check if the current LTI user is an instructor.
 * Shares the same update event as {@link useIsInLti}.
 */
export function useIsLtiInstructor(): boolean {
  const router = useRouter();
  const [isInstructor, setIsInstructor] = useState(detectIsLtiInstructor);

  useEffect(() => {
    setIsInstructor(detectIsLtiInstructor());
  }, [router.query.__debugLms, router.query.__debugLmsRole]);

  useEffect(() => {
    const handler = () => setIsInstructor(detectIsLtiInstructor());
    window.addEventListener(LTI_CONTEXT_UPDATE_EVENT, handler);
    return () => window.removeEventListener(LTI_CONTEXT_UPDATE_EVENT, handler);
  }, []);

  return isInstructor;
}
